import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { QueryOptions } from "apollo-client";
import gql from "graphql-tag";
import * as jp from "jsonpath";

import {
  ContextConfiguration
} from "../../context/config/context.config";
import * as fromContext from "../../context/context";
import {
  formatedTimeString
} from "../../util/utils";
import {
  AuthenticationService
} from "../auth/authentication.service";
import {
  BrandService
} from "../brand/brand.service";
import {
  DataService
} from "../data/data.service";
import {
  FormatterService
} from "../formatter/formatter.service";
import {
  LocaleService
} from "../locale/locale.service";
import { Logger } from "../logging/logger";
import {
  LoggingService
} from "../logging/logging.service";
import {
  NotificationsService
} from "../notifications/notifications.service";
import {
  TooltipService
} from "../tooltip/tooltip.service";
import { SupportService } from "../support/support.service";

export const APP_INITIALIZED = "APP_INITIALIZED";

const DATA = "data";
const PATH_TO_QUERY_CONFIGURATION_AUTH_CONFIG_PAYLOAD = "$..Authentication[*]";
const PATH_TO_QUERY_CONFIGURATION_LOCALES_PAYLOAD = "$..Locales[*]";
const PATH_TO_QUERY_CONFIGURATION_TOOLTIPS_PAYLOAD = "$..Tooltips[*]";
const PATH_TO_QUERY_CONFIGURATION_FORMATTERS_PAYLOAD = "$..Formatters[*]";
const PATH_TO_QUERY_CONFIGURATION_BRAND_SELECTORS_PAYLOAD = "$..BrandSelectors[*]";
const PATH_TO_CONTEXT_CONFIGURATION_PAYLOAD = "$..Configuration.Context";
const PATH_TO_NOTIFICATIONS_SERVICE_PAYLOAD = "$..Configuration.Notifications";
const PATH_TO_SUPPORT_SERVICE_PAYLOAD = "$..Configuration.Support";

const STARTUP_QUERY = `
query getStartupConfig($url: String!, $locale: String!) {
  Configuration {
    Authentication(urls: [$url]) {
      id
      guid
      clientID
      domain
      callbackURL
      responseType
      scope
      logoutURL
    }
    Formatters {
        id
        guid
        type
        category
        config
    }
    BrandSelectors {
      id
      type
      guid
      config
      Registrations {
        selector
        selectorProperty
      }
    }
    Tooltips {
      id
      guid
      type
      config
    }
    Locales(ids: [$locale]) {
        id
        decimal
        thousands
        grouping
        currency
        dateTime
        date
        time
        periods
        days
        shortDays
        months
        shortMonths
    }
    Support
    Notifications
    Context
  }
}
`;

const LOG_NAMESPACE: string = "StartupService";

@Injectable()
export class StartupService {

    private _startUpConfiguration: any;
    private _logger: Logger;

    constructor( private _store: Store<fromContext.Context>,
                 private _dataService: DataService,
                 private _formatterService: FormatterService,
                 private _brandService: BrandService,
                 private _tooltipService: TooltipService,
                 private _localeService: LocaleService,
                 private _authenticationService: AuthenticationService,
                 private _notificationsService: NotificationsService,
                 private _supportService: SupportService,
                 _loggingService: LoggingService
               ) {
      this._logger = new Logger(LOG_NAMESPACE, _loggingService);
    }

    public async load(): Promise<any> {
      // TODO When typescript pull request is adopted to allow userLanguage
      // we should remove this cast to any.  The cast is simply there to remove
      // a compiler error we know isn't really an error...
      // tslint:disable-next-line:max-line-length
      // Some browsers return lower case versions of either half of a locale so we lower case it and pass that in.  The back end is expecting a lower case file name and all files in the locale folder are lower cased names now.
      const userLocale = (navigator.language ? navigator.language : (navigator as any).userLanguage).toLowerCase();

      const queryOptions: QueryOptions = {
        query: gql`${STARTUP_QUERY}`,
        variables: {
          locale: userLocale,
          url: window.location.hostname + ((window.location.port) ? ':' + window.location.port :  '')
        }
      };
      this._logger.debug("Calling Data Service to get configuration for Startup service with Query and variables ",
        {query : STARTUP_QUERY, variables: queryOptions.variables});
      try {
        const data = await this._dataService.runQuery("StartUpService", queryOptions);
        this._logger.info("Received response from from GQL " + formatedTimeString());
        await this._instantiateStartupServices(data);
        this._logger.info("Loaded all services configurations from Startup Service " + formatedTimeString());
      } catch (err) {
        this._logger.error(err);
      }
    }

    public async _instantiateStartupServices(data: any) {
      this._logger.info("Instantiating StartUp Service");
      const configData: any = data[DATA];
      this._logger.debug("Instantiating StartUp Service with data ", {data: configData});
      this._authenticationService.init(jp.query(configData, PATH_TO_QUERY_CONFIGURATION_AUTH_CONFIG_PAYLOAD)[0]);
      this._localeService.init(jp.query(configData, PATH_TO_QUERY_CONFIGURATION_LOCALES_PAYLOAD)[0]);
      this._brandService.init(jp.query(configData, PATH_TO_QUERY_CONFIGURATION_BRAND_SELECTORS_PAYLOAD));
      this._formatterService.init(jp.query(configData, PATH_TO_QUERY_CONFIGURATION_FORMATTERS_PAYLOAD));
      this._tooltipService.init(jp.query(configData, PATH_TO_QUERY_CONFIGURATION_TOOLTIPS_PAYLOAD));
      this._notificationsService.init(jp.query(configData, PATH_TO_NOTIFICATIONS_SERVICE_PAYLOAD)[0] || {});
      await this._supportService.init(jp.query(configData, PATH_TO_SUPPORT_SERVICE_PAYLOAD)[0] || {});
      ContextConfiguration.init(jp.query(configData, PATH_TO_CONTEXT_CONFIGURATION_PAYLOAD)[0] || {});
      this._store.dispatch({type: APP_INITIALIZED});
    }

    get startupConfig(): any {
        return this._startUpConfiguration;
    }
}
